import React, { useEffect, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { getAppNames, getAppStatus } from "single-spa";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import {
  getMfeInfo,
  isShellOwnedPath,
  reportMfeInfo,
  subscribeMfeInfo,
} from "@/single-spa-config/apps";

interface SpaApp {
  name: string;
  status: string;
}

const SHELL_APP_NAME = "@observe/shell";

function isDebugEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  // Never show the debug badge under Playwright — it must not clutter e2e
  // screenshots. Playwright (like any WebDriver automation) sets
  // navigator.webdriver = true, which is the flag passed through to the page.
  if (typeof navigator !== "undefined" && navigator.webdriver) return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.endsWith("staging.observe.tech")) return true;
  return false;
}

export function DebugBadge() {
  const { location } = useRouterState();
  const host = useShellHost();
  const [enabled] = useState(() => isDebugEnvironment());
  const [open, setOpen] = useState(true);
  const [apps, setApps] = useState<SpaApp[]>([]);
  const [user, setUser] = useState(host.handle.auth.currentUser.get());
  const [lang, setLang] = useState(host.handle.i18n.language.get());
  const [siteIds, setSiteIds] = useState(
    host.handle.sites.selectedSiteIds.get(),
  );
  const [cageIds, setCageIds] = useState(
    host.handle.sites.selectedCageIds.get(),
  );
  const [range, setRange] = useState(host.handle.dateRange.range.get());

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      try {
        setApps(
          getAppNames().map((n) => ({
            name: n,
            status: getAppStatus(n) ?? "?",
          })),
        );
      } catch {
        setApps([]);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [enabled]);

  const shellMounted = useMemo(
    () => isShellOwnedPath(location.pathname),
    [location.pathname],
  );

  const appsWithShell = useMemo<SpaApp[]>(
    () => [
      {
        name: SHELL_APP_NAME,
        status: shellMounted ? "MOUNTED" : "NOT_MOUNTED",
      },
      ...apps,
    ],
    [apps, shellMounted],
  );

  useEffect(() => {
    if (!enabled) return;
    return host.handle.auth.currentUser.subscribe(setUser);
  }, [host, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return host.handle.i18n.language.subscribe(setLang);
  }, [host, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return host.handle.sites.selectedSiteIds.subscribe(setSiteIds);
  }, [host, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return host.handle.sites.selectedCageIds.subscribe(setCageIds);
  }, [host, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return host.handle.dateRange.range.subscribe(setRange);
  }, [host, enabled]);
  const [, bumpTechInfo] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    return subscribeMfeInfo(() => bumpTechInfo((n) => n + 1));
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    // The shell host isn't loaded via import(), so report its own runtime version.
    reportMfeInfo(SHELL_APP_NAME, {
      framework: "React",
      version: React.version,
    });
  }, [enabled]);

  const activeApp =
    appsWithShell.find((a) => a.status === "MOUNTED") ??
    appsWithShell.find((a) => a.status.includes("LOADING")) ??
    null;

  if (!enabled) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 99999,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: 11,
        color: "#F5F5F5",
        userSelect: "none",
      }}
    >
      {open && (
        <div
          style={{
            background: "rgba(0, 19, 31, 0.95)",
            border: "1px solid #02ADC3",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 8,
            minWidth: 260,
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
          }}
        >
          <div style={{ color: "#02ADC3", fontWeight: 600, marginBottom: 6 }}>
            MFE shell debug
          </div>
          <Row label="path" value={location.pathname} />
          <Row label="active app" value={activeApp?.name ?? "(none)"} />
          <Row label="user" value={user?.email ?? "—"} />
          <Row label="language" value={lang ?? "—"} />
          <Row label="sites" value={truncateList(siteIds)} />
          <Row label="cages" value={truncateList(cageIds)} />
          <Row label="date start" value={range.start ?? "—"} />
          <Row label="date end" value={range.end ?? "—"} />
          <div style={{ marginTop: 6, color: "#A0BCC5" }}>apps:</div>
          {appsWithShell.map((a) => {
            const tech = getMfeInfo(a.name);
            return (
              <div
                key={a.name}
                data-testid={`debug-badge-app-${a.name}`}
                data-status={a.status}
                style={{ display: "flex", gap: 6, alignItems: "baseline" }}
              >
                <span style={{ color: "#A0BCC5" }}>{a.name}</span>
                <span
                  style={{
                    color: a.status === "MOUNTED" ? "#09EF6E" : "#EEB902",
                  }}
                >
                  {a.status}
                </span>
                <span
                  style={{
                    color: "#637A88",
                    fontSize: 10,
                    marginLeft: "auto",
                  }}
                >
                  {tech?.framework ?? "unknown"} {tech?.version ?? "unknown"}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        title="MFE debug"
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "1px solid #02ADC3",
          background: open ? "#02ADC3" : "rgba(0, 19, 31, 0.85)",
          color: open ? "#00131F" : "#02ADC3",
          fontSize: 16,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
        }}
        data-testid="mfe-debug-toggle"
      >
        ⚙
      </button>
    </div>
  );
}

function truncateList(items: (string | number)[], max = 32): string {
  if (!items?.length) return "—";
  const joined = items.join(", ");
  if (joined.length <= max) return joined;
  let acc = "";
  for (const item of items) {
    const next = acc ? `${acc}, ${item}` : String(item);
    if (next.length + 4 > max) break;
    acc = next;
  }
  return `${acc || items[0]}, ...`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <span style={{ color: "#A0BCC5", minWidth: 70 }}>{label}</span>
      <span style={{ color: "#F5F5F5", wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}
